import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

function fmt(t) {
  const [h,m] = t.split(':').map(Number);
  return `${h%12||12}:${String(m).padStart(2,'0')} ${h>=12?'PM':'AM'}`;
}
function getNext7Days() {
  return Array.from({length:7},(_,i)=>{ const d=new Date(); d.setDate(d.getDate()+i); return d.toISOString().slice(0,10); });
}

export default function WeekView() {
  const [data, setData]    = useState({});
  const [loading, setLoad] = useState(true);
  const days = getNext7Days();

  const load = useCallback(async () => {
    const results = {};
    await Promise.all(days.map(async date => {
      try { const r = await api.getReservations({ date }); results[date] = r.filter(x=>x.status!=='cancelled').sort((a,b)=>a.time_slot.localeCompare(b.time_slot)); }
      catch { results[date]=[]; }
    }));
    setData(results); setLoad(false);
  }, []); // eslint-disable-line

  useEffect(() => {
    load();
    window.addEventListener('seva:refresh', load);
    return () => window.removeEventListener('seva:refresh', load);
  }, [load]);

  const totalRes    = days.reduce((s,d)=>s+(data[d]||[]).length,0);
  const totalCovers = days.reduce((s,d)=>s+(data[d]||[]).reduce((x,r)=>x+r.party_size,0),0);

  if (loading && Object.keys(data).length === 0) return <div style={s.loading}>Loading week…</div>;

  return (
    <div>
      <h1 style={s.h1}>Next 7 Days</h1>
      <p style={s.sub}>Quick view of upcoming reservations.</p>

      <div style={s.statsRow}>
        <div style={s.stat} className="glass-card-hover t">
          <div style={s.sv}>{totalRes}</div><div style={s.sl}>Reservations this week</div>
        </div>
        <div style={s.stat} className="glass-card-hover t">
          <div style={{ ...s.sv, color:'var(--accent)' }}>{totalCovers}</div><div style={s.sl}>Total covers</div>
        </div>
      </div>

      <div style={s.grid}>
        {days.map(date => {
          const resos = data[date] || [];
          const today = new Date().toISOString().slice(0,10);
          const isToday = date === today;
          const d = new Date(date+'T12:00:00Z');
          const dayName = isToday ? 'Today' : d.toLocaleDateString('en-US',{weekday:'short',timeZone:'UTC'});
          const dateStr = d.toLocaleDateString('en-US',{month:'short',day:'numeric',timeZone:'UTC'});

          return (
            <div key={date} style={{ ...s.col, ...(isToday ? s.todayCol : {}) }}>
              <div style={{ ...s.colHead, ...(isToday ? s.todayHead : {}) }}>
                <div style={{ ...s.colDay, ...(isToday ? { color:'rgba(255,255,255,0.7)' } : {}) }}>{dayName}</div>
                <div style={{ ...s.colDate, ...(isToday ? { color:'#fff' } : {}) }}>{dateStr}</div>
                <div style={{ ...s.colCount, ...(isToday ? s.todayCount : {}) }}>
                  {resos.length===0 ? 'No bookings' : `${resos.length} booking${resos.length>1?'s':''}`}
                </div>
              </div>
              <div style={s.colBody}>
                {resos.length===0 ? <div style={s.noRes}>—</div> : resos.map(r => (
                  <div key={r.id} style={{ ...s.resCard, ...(isToday ? s.todayResCard : {}) }}>
                    <div style={s.resTime}>{fmt(r.time_slot)}</div>
                    <div style={s.resName}>{r.customer_name}</div>
                    <div style={s.resMeta}>
                      <span style={s.chip}>{r.tables_display || r.table_label}</span>
                      <span style={{ ...s.chip }}>{r.party_size}p</span>
                      {r.source==='ai' && <span style={{ ...s.chip, fontWeight:700 }}>AI</span>}
                    </div>
                    {r.special_requests && <div style={s.note}>{r.special_requests}</div>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const s = {
  loading:   { color:'var(--text-muted)', padding:40, fontSize:15 },
  h1:        { fontSize:28, fontWeight:800, color:'var(--text-primary)', margin:'0 0 6px', letterSpacing:'-0.5px' },
  sub:       { fontSize:14, color:'var(--text-muted)', marginBottom:24 },
  statsRow:  { display:'flex', gap:16, marginBottom:22 },
  stat:      { background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:18, padding:'18px 26px', minWidth:180, boxShadow:'0 1px 4px rgba(15,23,42,0.06)' },
  sv:        { fontSize:30, fontWeight:800, color:'var(--accent)', marginBottom:4 },
  sl:        { fontSize:12, color:'var(--text-muted)', fontWeight:500 },
  grid:      { display:'grid', gridTemplateColumns:'repeat(7,minmax(0,1fr))', gap:10 },
  col:       { background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:16, overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 1px 3px rgba(15,23,42,0.05)' },
  todayCol:  { boxShadow:'0 0 0 2px var(--accent)' },
  colHead:   { padding:'12px 10px 10px', background:'var(--section-bg)', borderBottom:'1px solid var(--section-border)' },
  todayHead: { background:'var(--accent)' },
  colDay:    { fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:'0.1em', color:'var(--text-muted)', marginBottom:2 },
  colDate:   { fontSize:14, fontWeight:700, color:'var(--text-primary)' },
  colCount:  { display:'inline-block', marginTop:5, fontSize:9, fontWeight:700, background:'var(--chip-bg)', color:'var(--chip-text)', borderRadius:20, padding:'2px 7px' },
  todayCount:{ background:'rgba(255,255,255,0.25)', color:'#fff' },
  colBody:   { padding:'8px 7px', display:'flex', flexDirection:'column', gap:5, flex:1 },
  noRes:     { color:'var(--text-muted)', fontSize:16, textAlign:'center', padding:'14px 0', opacity:0.5 },
  resCard:   { background:'var(--section-bg)', borderRadius:10, padding:'8px 9px', borderLeft:'2px solid var(--card-border)' },
  todayResCard:{ borderLeft:'2px solid var(--accent)' },
  resTime:   { fontSize:10, fontWeight:700, color:'var(--text-muted)', marginBottom:2 },
  resName:   { fontSize:12, fontWeight:600, color:'var(--text-primary)', marginBottom:4, lineHeight:1.3 },
  resMeta:   { display:'flex', gap:3, flexWrap:'wrap' },
  chip:      { background:'var(--chip-bg)', color:'var(--chip-text)', borderRadius:4, padding:'2px 5px', fontSize:9, fontWeight:600 },
  note:      { fontSize:9, color:'var(--text-muted)', marginTop:3 },
};
